import { useQuery } from '@tanstack/react-query';
import { fetchCmsPage } from '@/services/cms-service';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  CmsPageResponse,
  CmsComponent,
  CmsFooterSettings,
} from '@/services/cms-types';

/**
 * React Query hook to fetch CMS page data for the landing page.
 * Includes the current locale in the query key so that changing
 * language via LanguageContext automatically refetches the page
 * content in the selected language.
 *
 * @param pageSlug - The page path slug (defaults to '/' for home).
 */
export function useCmsPage(pageSlug = '/') {
  const { language } = useLanguage();

  const query = useQuery<CmsPageResponse>({
    queryKey: ['cms-page', pageSlug, language],
    queryFn: () => fetchCmsPage(pageSlug, language),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  return {
    ...query,
    seo: query.data?.seo ?? null,
    /**
     * Finds a component in the main zone by its section slug and component type.
     */
    findComponent: (sectionSlug: string, type?: string): CmsComponent | undefined => {
      return query.data?.zones.main.find(
        (c) => c.sectionSlug === sectionSlug && (type ? c.type === type : true)
      );
    },
    /**
     * Finds all components in the main zone belonging to a given section.
     */
    findSectionComponents: (sectionSlug: string): CmsComponent[] => {
      return query.data?.zones.main.filter((c) => c.sectionSlug === sectionSlug) ?? [];
    },
    /**
     * Finds the first component in the footer zone by type.
     */
    findFooterComponent: (type?: string): CmsComponent | undefined => {
      return query.data?.zones.footer.find(
        (c) => (type ? c.type === type : true)
      );
    },
  };
}

/**
 * Type guard to check if component settings are Footer settings.
 */
export function isFooterSettings(
  settings: CmsComponent['settings']
): settings is CmsFooterSettings {
  return settings !== null && 'copyright' in settings && 'privacyPolicyUrl' in settings;
}
